import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Collapse,
  Typography,
  Grid,
  IconButton,
  List,
  ListItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Breadcrumbs,
  Link
} from '@mui/material';
import { LinearProgress, Snackbar, Alert } from '@mui/material';
import { Close, ArrowBack, InfoOutlined, VideoLibraryOutlined, MenuBookOutlined, GroupsOutlined, AssignmentOutlined } from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FolderIcon, MonitorPlay, ClipboardList } from 'lucide-react';
import coursesService from '../../../services/CoursesService';
import CourseTitleImageRow from './NewCourseForm/CourseTitleImageRow';
import CourseDescription from './NewCourseForm/CourseDescription';
import InstructorInfo from './NewCourseForm/InstructorInfo';
import VideosMaterialsRow from './NewCourseForm/VideosMaterialsRow';
import CourseGuide from './NewCourseForm/CourseGuide';
import CourseAudienceSection from './NewCourseForm/CourseAudienceSection';
import FormSection, { BRAND_COLOR } from './NewCourseForm/FormSection';
import QuestionnairesSection from './NewCourseForm/QuestionnairesSection';
import ActionButtonsRow from './NewCourseForm/ActionButtonsRow';
import UploadModal from './NewCourseForm/UploadModal';
import ProgressFeedback from '../ProgressFeedback';
import { getVideos } from '../../../services/VideosService';
import documentsService from '../../../services/DocumentsService';
import questionnairesService from '../../../services/questionnairesService';
import teacherService from '../../../services/TeacherService';
import {
  formatVideoDuration,
  mapRepositoryVideo,
  probeVideoDuration,
  resolveMediaUrl,
} from '../../../utils/videoMediaUtils';

const VALIDATION_RULES = {
  title: { min: 1, max: 200, message: 'El título no puede estar vacío.' },
  description: { min: 1, max: 10000, message: 'La descripción no puede estar vacía.' },
  guide: { min: 1, max: 10000, message: 'La guía no puede estar vacía.' }
};

const stripHtml = (text) => {
  if (typeof text !== 'string') return ''; // Maneja casos donde text no es string
  return text.replace(/<[^>]+>/g, '').trim() || '';
};

const validateField = (value, rules) => {
  if (value === undefined || value === null) return rules.message;
  const text = stripHtml(value);
  if (text.length < rules.min) {
    return rules.message;
  }
  if (rules.max && text.length > rules.max) {
    return `Máximo ${rules.max} caracteres.`;
  }
  return null;
};

const NewCourseForm = ({ 
  show, 
  editMode = false, 
  courseData = null, 
  onUpdate,
  onCancel,
  onCreate
}) => {

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    teacher: null,
    image: null,
    thumbnailRemoved: false,
    guide: '',
    teacherDescription: '',
    certificate: null,
    isPublic: false,
    isWelcome: false
  });

  const [audienceTargets, setAudienceTargets] = useState({});
  const [selectedLabelGroupIds, setSelectedLabelGroupIds] = useState([]);

  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);

  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [questionnaireModalOpen, setQuestionnaireModalOpen] = useState(false);

  const [currentVideoRow, setCurrentVideoRow] = useState(null);
  const [videoSearch, setVideoSearch] = useState('');
  const [videoModalSelection, setVideoModalSelection] = useState(null);

  const [currentDocumentRow, setCurrentDocumentRow] = useState(null);
  const [documentSearch, setDocumentSearch] = useState('');
  const [documentModalSelection, setDocumentModalSelection] = useState(null);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState(null); // 'video' or 'material'
  const [uploadRow, setUploadRow] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFileName, setUploadFileName] = useState('');

  const [errors, setErrors] = useState({});

  // Add refs for each field
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const teacherDescriptionRef = useRef(null);
  const guideRef = useRef(null);



  const [videoLibrary, setVideoLibrary] = useState([]);
  const [documentLibrary, setDocumentLibrary] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);

  const [capacitadores, setCapacitadores] = useState([]);

  
  // Populate form data when in edit mode (solo al cambiar de curso, no en cada render)
  useEffect(() => {
    if (editMode && courseData) {
      populateFormWithCourseData(courseData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, courseData?.id || courseData?.Id]);

  // Handle teacher population when capacitadores loads after course data
  useEffect(() => {
    if (editMode && courseData && capacitadores.length > 0 && !formData.teacher) {
      const teacherId = courseData.teacherId || courseData.TeacherId || courseData.teached_id || courseData.teacher?.id;
      const selectedTeacher = teacherId 
        ? capacitadores.find(c => c.id === teacherId) || null
        : null;
      
      if (selectedTeacher) {
        setFormData(prev => ({
          ...prev,
          teacher: selectedTeacher
        }));
      }
    }
  }, [capacitadores, editMode, courseData, formData.teacher]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        //profesores
        const capacitadoresResponse = await teacherService.getTeachers();
        const formattedCapacitadores = capacitadoresResponse.map(capacitador => ({
          id: capacitador.id,
          nombre: capacitador.name,
          especialidad: capacitador.description,
          experiencia: "n/a",
        }));
        setCapacitadores(formattedCapacitadores);

        // Videos del repositorio (miniatura + duración)
        const videosResponse = await getVideos();
        const formattedVideos = (videosResponse || []).map(mapRepositoryVideo);
        setVideoLibrary(formattedVideos);
  
        // Material del repositorio: PDF, Audio/Podcast y Links
        const documentsResponse = await documentsService.getDocuments();
        const formattedDocuments = (documentsResponse || []).map((doc) => {
          const contentType = (doc.contentType || doc.ContentType || 'document').toLowerCase();
          const typeLabel =
            contentType === 'audio' || contentType === 'podcast'
              ? 'AUDIO'
              : contentType === 'link' || contentType === 'enlace'
                ? 'LINK'
                : ((doc.path || '').split('.').pop() || 'PDF').toUpperCase();
          return {
            id: doc.id,
            title: doc.title,
            type: typeLabel,
            contentType,
            size: '---',
            path: doc.path || doc.Path || '',
            externalUrl: doc.externalUrl || doc.ExternalUrl || '',
            thumbnail: resolveMediaUrl(doc.thumbnailPath || doc.ThumbnailPath),
            thumbnailPath: doc.thumbnailPath || doc.ThumbnailPath || null,
          };
        });
        setDocumentLibrary(formattedDocuments);
  
        // Cuestionarios
        const questionnairesResponse = await questionnairesService.getQuestionnaires();
        const formattedQuestionnaires = questionnairesResponse.map(q => ({
          id: q.id,
          title: q.title,
          description: q.description,
        }));
        setQuestionnaires(formattedQuestionnaires);
  
      } catch (error) {
        console.error('Error al cargar recursos:', error);
      }
    };
  
    fetchData();
  }, []);

  // Cleanup function para limpiar URLs de preview cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (formData.imagePreview) {
        URL.revokeObjectURL(formData.imagePreview);
      }
    };
  }, [formData.imagePreview]);

  // Function to populate form with existing course data
  const populateFormWithCourseData = (course) => {
    // Find the teacher from the loaded capacitadores
    const teacherId = course.teacherId || course.TeacherId || course.teached_id || course.teacher?.id;
    
    // Debug: Log teacher information
    console.log('Edit mode - Teacher debug:', {
      teacherId,
      courseTeacherId: course.teacherId,
      courseTeachedId: course.teached_id,
      capacitadoresLength: capacitadores.length,
      capacitadores: capacitadores
    });
    
    let selectedTeacher = null;
    if (teacherId && capacitadores.length > 0) {
      selectedTeacher = capacitadores.find(c => c.id === teacherId);
      console.log('Found teacher:', selectedTeacher);
      
      // If teacher not found in capacitadores, fetch teacher details
      if (!selectedTeacher) {
        console.log('Teacher not found in capacitadores, will need to fetch teacher details');
        // You might need to call teacherService.getTeacher(teacherId) here
      }
    }

    // Handle image URL with environment variable
    const imagePath = course.thumbnailUrl || course.thumbnail || course.Thumbnail;
    let imageUrl = null;
    if (imagePath) {
      // Handle cases where imagePath might already start with '/'
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      imageUrl = `${process.env.REACT_APP_LOCATION}${cleanPath}`;
    }

    const newFormData = {
      // Título en texto plano (antes podía venir con HTML del editor Quill)
      title: stripHtml(course.title || course.Title || ''),
      description: course.description || course.Description || '',
      guide: course.objective || course.Objective || course.guide || course.Guide || '',
      teacherDescription: course.teacherDescription || course.TeacherDescription || '',
      teacher: selectedTeacher,
      image: imageUrl, // Keep the URL for existing images
      imagePreview: imageUrl, // Set preview to show existing image
      thumbnailRemoved: false,
      certificate: course.certificate || course.Certificate || null,
      isPublic: Boolean(course.isPublic ?? course.IsPublic),
      isWelcome: Boolean(course.isWelcome ?? course.IsWelcome)
    };

    setFormData(newFormData);

    // Populate videos if they exist
    const courseVideos = course.videos || course.Videos || [];
    console.log('=== Loading course videos for edit ===');
    console.log('Course videos from API:', courseVideos);
    
    if (courseVideos.length > 0) {
      const formattedVideos = courseVideos.map((video) => {
        const mapped = mapRepositoryVideo(video);
        return {
          name: mapped.title || video.title || video.Title || '',
          selectedItem: mapped,
          source: 'existente',
          file: null,
          thumbnail: mapped.thumbnail,
          duration: mapped.duration,
          durationSeconds: mapped.durationSeconds,
        };
      });
      console.log('Formatted videos for edit:', formattedVideos);
      setVideos(formattedVideos);
    } else {
      console.log('No existing videos found, setting empty array');
      setVideos([]);
    }

    // Populate documents if they exist
    const courseDocuments = course.documents || course.Documents || [];
    if (courseDocuments.length > 0) {
      const formattedDocuments = courseDocuments.map((doc) => {
        const contentType = (doc.contentType || doc.ContentType || 'document').toLowerCase();
        return {
          name: doc.title || doc.Title || '',
          selectedItem: {
            ...doc,
            id: doc.id ?? doc.Id,
            contentType,
            thumbnail: resolveMediaUrl(doc.thumbnailPath || doc.ThumbnailPath),
            externalUrl: doc.externalUrl || doc.ExternalUrl || '',
          },
          source: 'existente',
          file: null,
          thumbnail: resolveMediaUrl(doc.thumbnailPath || doc.ThumbnailPath),
          contentType,
        };
      });
      setDocuments(formattedDocuments);
    } else {
      setDocuments([]);
    }

    // Populate questionnaires if they exist
    const courseQuestionnaires = course.questionnaires || course.Questionnaires || [];
    if (courseQuestionnaires.length > 0) {
      setSelectedQuestionnaires(courseQuestionnaires);
    } else {
      setSelectedQuestionnaires([]);
    }

    // Las etiquetas manuales se marcan aquí; las que aportan los grupos se derivan de selectedLabelGroupIds
    const courseLabelIds =
      course.manualLabelIds || course.ManualLabelIds ||
      course.LabelIds || course.labelIds || course.labels || course.Labels || [];
    setSelectedLabelGroupIds(course.labelGroupIds || course.LabelGroupIds || []);
    
    // Debug: Log label information
    console.log('Edit mode - Labels debug:', {
      courseLabelIds,
      courseLabels: course.labels,
      courseLabelIdsProperty: course.LabelIds,
      courseData: course
    });
    
    const newAudienceTargets = {};
    
    if (courseLabelIds.length > 0) {
      courseLabelIds.forEach(labelId => {
        // If it's an object with id, extract the id, otherwise use the value directly
        const id = typeof labelId === 'object' ? (labelId.id || labelId.Id) : labelId;
        if (id) {
          newAudienceTargets[id] = true;
        }
      });
    }
    
    setAudienceTargets(newAudienceTargets);
  };
  


  const handleTextChange = async (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value || ''
    }));
  };


  const handleTeacherChange = (capacitador) => {
    setFormData(prev => ({
      ...prev,
      teacher: capacitador || null
    }));
  };


  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Limpiar la preview anterior si existe
      if (formData.imagePreview) {
        URL.revokeObjectURL(formData.imagePreview);
      }
      
      const previewUrl = URL.createObjectURL(file);
  
      setFormData({
        ...formData,
        image: file,               // 🔴 Aquí guardas el File real
        imagePreview: previewUrl,  // 🔵 Este es solo para previsualización
        thumbnailRemoved: false
      });
    }
  };
  


  // Simple handlers for the form
  const listHandlers = (items, setItems) => ({
    add: () => {
      setItems([...items, { name: "", file: null, selectedItem: null, source: null }]);
    }
  });

  const videoHandlers = listHandlers(videos, setVideos);
  const documentHandlers = listHandlers(documents, setDocuments);

  const openQuestionnaireModal = () => {
    setQuestionnaireModalOpen(true);
  };

  const closeQuestionnaireModal = () => {
    setQuestionnaireModalOpen(false);
  };

  const handleQuestionnaireToggle = (questionnaire) => {
    const currentIndex = selectedQuestionnaires.findIndex(q => q.id === questionnaire.id);

    if (currentIndex === -1) {
      setSelectedQuestionnaires([...selectedQuestionnaires, questionnaire]);
    } else {
      setSelectedQuestionnaires(selectedQuestionnaires.filter(q => q.id !== questionnaire.id));
    }
  };

  const isQuestionnaireSelected = (id) => {
    return selectedQuestionnaires.some(q => q.id === id);
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
    setCurrentVideoRow(null);
    setVideoSearch('');
    setVideoModalSelection(null);
  };

  const closeDocumentModal = () => {
    setDocumentModalOpen(false);
  };

  const handleVideoSelection = () => {
    if (currentVideoRow !== null && videoModalSelection) {
      const newVideos = [...videos];
      while (newVideos.length <= currentVideoRow) {
        newVideos.push({ name: '', file: null, selectedItem: null, source: null });
      }
      newVideos[currentVideoRow] = {
        ...newVideos[currentVideoRow],
        name: videoModalSelection.title,
        selectedItem: videoModalSelection,
        source: 'existente',
        file: null,
        thumbnail: videoModalSelection.thumbnail || null,
        duration: videoModalSelection.duration || formatVideoDuration(videoModalSelection.durationSeconds),
        durationSeconds: videoModalSelection.durationSeconds ?? null,
      };
      setVideos(newVideos);
    }
    closeVideoModal();
  };

  // Al abrir el modal, obtener duración real desde metadatos si aún no está
  useEffect(() => {
    if (!videoModalOpen) return undefined;

    let cancelled = false;

    (async () => {
      const snapshot = await new Promise((resolve) => {
        setVideoLibrary((prev) => {
          resolve(prev);
          return prev;
        });
      });

      const needsProbe = (snapshot || []).filter(
        (v) => v?.videoUrl && (v.durationSeconds == null || v.durationSeconds <= 0)
      );
      if (!needsProbe.length || cancelled) return;

      const updates = await Promise.all(
        needsProbe.map(async (video) => {
          const seconds = await probeVideoDuration(video.videoUrl);
          return { id: video.id, seconds };
        })
      );

      if (cancelled) return;

      setVideoLibrary((prev) =>
        prev.map((video) => {
          const found = updates.find((u) => u.id === video.id && u.seconds != null);
          if (!found) return video;
          return {
            ...video,
            durationSeconds: found.seconds,
            duration: formatVideoDuration(found.seconds),
          };
        })
      );

      setVideoModalSelection((prev) => {
        if (!prev) return prev;
        const found = updates.find((u) => u.id === prev.id && u.seconds != null);
        if (!found) return prev;
        return {
          ...prev,
          durationSeconds: found.seconds,
          duration: formatVideoDuration(found.seconds),
        };
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [videoModalOpen]);

  const handleDocumentSelection = () => {
    setDocumentModalOpen(false);
  };

  // Function to scroll to field with error
  const scrollToError = (fieldName) => {
    const refs = {
      title: titleRef,
      description: descriptionRef,
      teacherDescription: teacherDescriptionRef,
      guide: guideRef
    };

    const ref = refs[fieldName];
    if (ref?.current) {
      ref.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
    }
  };

  
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [alert, setAlert] = useState({ open: false, type: '', message: '' });
  
  const handleSubmit = async (status = 0) => {
    const newErrors = {};
  
    // Validation of fields
    Object.entries(VALIDATION_RULES).forEach(([field, rules]) => {
      const error = validateField(formData[field], rules);
      if (error) {
        newErrors[field] = error;
      }
    });
  
    setErrors(newErrors);
  
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      scrollToError(firstErrorField);
      return;
    }


  
    setIsLoading(true);
    setProgress(20); // Start progress
  
    try {
      // Prepare form data with status
      const formDataWithStatus = {
        ...formData,
        status: status,
        labelGroupIds: selectedLabelGroupIds
      };
      if (editMode) {
        // Update existing course
        console.log('=== Submitting course update ===');
        console.log('Course ID:', courseData.id);
        console.log('Videos to send:', videos);
        console.log('Documents to send:', documents);
        
        const updatedCourse = await coursesService.updateCourse(
          courseData.id,
          formDataWithStatus,
          videos,
          documents,
          audienceTargets,
          selectedQuestionnaires
        );
  
        setProgress(80);
        await new Promise((resolve) => setTimeout(resolve, 300));
  
        setAlert({
          open: true,
          type: 'success',
          message: status === 1 ? '¡Curso publicado exitosamente!' : '¡Curso guardado exitosamente!',
        });
  
        setProgress(100);
        
        // Call onUpdate callback if provided
        if (onUpdate) {
          onUpdate(updatedCourse);
        }
      } else {
        // Create new course
        const createdCourse = await coursesService.createCourse(
          formDataWithStatus,
          videos,
          documents,
          audienceTargets,
          selectedQuestionnaires
        );
  
        setProgress(80);
        await new Promise((resolve) => setTimeout(resolve, 300));
  
        setAlert({
          open: true,
          type: 'success',
          message: status === 1 ? '¡Curso publicado exitosamente!' : '¡Curso guardado exitosamente!',
        });

        // Call onCreate callback if provided
        if (onCreate) {
          onCreate(createdCourse);
        }

        // Optionally reset or navigate
        resetForm();

        setProgress(100);
      }
    } catch (error) {
      console.error(`Error al ${editMode ? 'actualizar' : 'crear'} el curso:`, error);
      setAlert({
        open: true,
        type: 'error',
        message: error.message || `Ocurrió un error al ${editMode ? 'actualizar' : 'crear'} el curso.`,
      });
      setProgress(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handlers for different submission types
  const handleSave = () => handleSubmit(0); // Save as draft
  const handlePublish = () => handleSubmit(1); // Publish
  
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      guide: '',
      teacher: '',
      image: null,
      imagePreview: null,
      thumbnailRemoved: false,
      isPublic: false,
      isWelcome: false
    });
    setVideos([]);
    setDocuments([]);
    setSelectedQuestionnaires([]);
    setAudienceTargets({});
    setSelectedLabelGroupIds([]);
  };
  

  // Add reorder functions for videos and documents
  const reorderVideos = (fromIndex, toIndex) => {
    const newVideos = [...videos];
    const [movedItem] = newVideos.splice(fromIndex, 1);
    newVideos.splice(toIndex, 0, movedItem);
    setVideos(newVideos);
  };

  const reorderDocuments = (fromIndex, toIndex) => {
    const newDocuments = [...documents];
    const [movedItem] = newDocuments.splice(fromIndex, 1);
    newDocuments.splice(toIndex, 0, movedItem);
    setDocuments(newDocuments);
  };

  return (
    <Collapse in={show} sx={{ mt: 3, width: '100%' }}>
      <Box sx={{ maxWidth: '1400px', width: '100%', mx: 'auto' }}>
        <Box
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            color: '#fff',
            background: `linear-gradient(135deg, ${BRAND_COLOR} 0%, #88B24F 100%)`,
          }}
        >
          {editMode && (
            <Breadcrumbs sx={{ mb: 1.5, color: 'rgba(255,255,255,0.8)', '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.8)' } }}>
              <Link
                component="button"
                onClick={() => onCancel && onCancel()}
                sx={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', cursor: 'pointer' }}
              >
                Gestionar cursos
              </Link>
              <Typography sx={{ color: '#fff' }}>Editar curso</Typography>
            </Breadcrumbs>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {editMode
                  ? stripHtml(formData.title) || stripHtml(courseData?.title) || 'Editar curso'
                  : 'Crear nuevo curso'}
              </Typography>
              <Typography sx={{ opacity: 0.9, mt: 0.5 }}>
                {editMode
                  ? `Última modificación: ${courseData?.updatedAt ? new Date(courseData.updatedAt).toLocaleDateString() : 'N/A'}`
                  : 'Completa la información por secciones. Puedes guardar como borrador en cualquier momento.'}
              </Typography>
            </Box>
            {editMode && (
              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                onClick={() => onCancel && onCancel()}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)', boxShadow: 'none' } }}
              >
                Volver a la lista
              </Button>
            )}
          </Box>
        </Box>

        <FormSection
          step={1}
          icon={<InfoOutlined />}
          title="Información general"
          subtitle="Título, imagen, descripción y capacitador del curso"
        >
          <CourseTitleImageRow
            ref={titleRef}
            titleValue={formData.title || ''}
            onTitleChange={value => handleTextChange('title', value)}
            image={formData.image}
            imagePreview={formData.imagePreview}
            onImageUpload={handleImageUpload}
            onRemoveImage={() => {
              // Limpiar la URL de la preview para evitar memory leaks
              if (formData.imagePreview) {
                URL.revokeObjectURL(formData.imagePreview);
              }
              setFormData({ ...formData, image: null, imagePreview: null, thumbnailRemoved: true });
            }}
            error={errors.title}
          />
          <FormControlLabel
            sx={{ mb: 2, alignItems: 'flex-start', ml: 0 }}
            control={
              <Checkbox
                checked={Boolean(formData.isPublic)}
                onChange={(e) => setFormData((prev) => ({ ...prev, isPublic: e.target.checked }))}
                sx={{ color: BRAND_COLOR, '&.Mui-checked': { color: BRAND_COLOR }, pt: 0.5 }}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Curso público
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Visible en el listado de cursos de la página de inicio aunque el usuario no esté logueado. Para inscribirse sí deberá iniciar sesión.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            sx={{ mb: 2, alignItems: 'flex-start', ml: 0 }}
            control={
              <Checkbox
                checked={Boolean(formData.isWelcome)}
                onChange={(e) => setFormData((prev) => ({ ...prev, isWelcome: e.target.checked }))}
                sx={{ color: BRAND_COLOR, '&.Mui-checked': { color: BRAND_COLOR }, pt: 0.5 }}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Curso de bienvenida
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Solo puede haber un curso de bienvenida activo. Al publicarlo se matriculará automáticamente a todos los usuarios y deberán completarlo antes de acceder a otros cursos.
                </Typography>
              </Box>
            }
          />
          <CourseDescription
            ref={descriptionRef}
            value={formData.description || ''}
            onChange={value => handleTextChange('description', value)}
            error={errors.description}
          />
          <InstructorInfo
            ref={teacherDescriptionRef}
            capacitadores={capacitadores || []}
            teacherValue={formData.teacher || null}
            onTeacherChange={handleTeacherChange}
            error={errors.teacherDescription}
          />
        </FormSection>

        <FormSection
          step={2}
          icon={<VideoLibraryOutlined />}
          title="Contenido"
          subtitle="Videos y materiales de apoyo. Arrastra para cambiar el orden."
        >
          <VideosMaterialsRow
            videos={videos}
            setVideos={setVideos}
            documents={documents}
            setDocuments={setDocuments}
            videoHandlers={videoHandlers}
            documentHandlers={documentHandlers}
            onSelectVideo={idx => { setCurrentVideoRow(idx); setVideoModalOpen(true); setVideoModalSelection(null); }}
            onUploadVideo={idx => { setUploadType('video'); setUploadRow(idx); setUploadModalOpen(true); setUploadFile(null); setUploadFileName(''); }}
            onEditVideo={() => {}}
            onDeleteVideo={idx => { if (videos[idx]) { setVideos(videos.filter((_, i) => i !== idx)); } }}
            onSelectMaterial={idx => { setCurrentDocumentRow(idx); setDocumentModalOpen(true); setDocumentModalSelection(null); }}
            onUploadMaterial={idx => { setUploadType('material'); setUploadRow(idx); setUploadModalOpen(true); setUploadFile(null); setUploadFileName(''); }}
            onEditMaterial={() => {}}
            onDeleteMaterial={idx => { if (documents[idx]) { setDocuments(documents.filter((_, i) => i !== idx)); } }}
            onReorderVideos={reorderVideos}
            onReorderDocuments={reorderDocuments}
          />
        </FormSection>

        <FormSection
          step={3}
          icon={<MenuBookOutlined />}
          title="Guía del curso"
          subtitle="Orientaciones para el participante"
        >
          <CourseGuide
            ref={guideRef}
            value={formData.guide || ''}
            onChange={value => handleTextChange('guide', value)}
            error={errors.guide}
          />
        </FormSection>

        <FormSection
          step={4}
          icon={<GroupsOutlined />}
          title="¿A quién va dirigido?"
          subtitle="Grupos de etiquetas, perfiles y etiquetas de interés del público objetivo"
        >
          <CourseAudienceSection
            audienceTargets={audienceTargets}
            onAudienceChange={(labelId, isChecked) => {
              setAudienceTargets(prev => ({
                ...prev,
                [labelId]: isChecked
              }));
            }}
            selectedGroupIds={selectedLabelGroupIds}
            onGroupsChange={setSelectedLabelGroupIds}
          />
        </FormSection>

        <FormSection
          step={5}
          icon={<AssignmentOutlined />}
          title="Evaluación"
          subtitle="Cuestionarios que el participante deberá completar"
        >
          <QuestionnairesSection
            selectedQuestionnaires={selectedQuestionnaires}
            openQuestionnaireModal={openQuestionnaireModal}
          />
        </FormSection>

        <ActionButtonsRow
          onSave={handleSave}
          onPublish={handlePublish}
          editMode={editMode}
          onCancel={onCancel}
        />
      </Box>
      <UploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onOk={() => {
          if (uploadRow !== null && uploadFile) {
            if (uploadType === 'video') {
              console.log('=== Adding new video ===');
              console.log('Upload row:', uploadRow);
              console.log('Upload file:', uploadFile);
              console.log('Current videos:', videos);
              
              const newVideos = [...videos];
              newVideos[uploadRow] = {
                ...newVideos[uploadRow],
                name: uploadFile.name,
                file: uploadFile,
                source: 'nuevo',
              };
              
              console.log('Updated videos array:', newVideos);
              setVideos(newVideos);
            } else if (uploadType === 'material') {
              const newDocs = [...documents];
              newDocs[uploadRow] = {
                ...newDocs[uploadRow],
                name: uploadFile.name,
                file: uploadFile,
                source: 'nuevo',
              };
              setDocuments(newDocs);
            }
          }
          setUploadModalOpen(false);
        }}
        fileType={uploadType}
        file={uploadFile}
        fileName={uploadFileName}
        onFileChange={e => {
          const file = e.target.files[0];
          if (file) {
            setUploadFile(file);
            setUploadFileName(file.name);
          }
        }}
      />

      {/* Dialogs */}
      <Dialog open={videoModalOpen} onClose={closeVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Seleccionar Video
          <IconButton onClick={closeVideoModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button variant="contained" sx={{ mr: 2, background: '#4DABA4', color: '#fff', fontWeight: 600 }}>
              Buscar
            </Button>
            <TextField
              placeholder="Filtra por el nombre del video"
              value={videoSearch}
              onChange={e => setVideoSearch(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {videoLibrary
              .filter((v) => (v.title || '').toLowerCase().includes(videoSearch.toLowerCase()))
              .map((video) => {
                const selected = videoModalSelection && videoModalSelection.id === video.id;
                return (
                  <Box
                    key={video.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      mb: 1,
                      borderRadius: 1,
                      background: selected ? '#b6e2d8' : '#fff',
                      border: selected ? '2px solid #4DABA4' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setVideoModalSelection(video)}
                  >
                    <Box
                      sx={{
                        width: 96,
                        height: 54,
                        flexShrink: 0,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: '#eee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {video.thumbnail ? (
                        <Box
                          component="img"
                          src={video.thumbnail}
                          alt={video.title}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <MonitorPlay size={22} color="#9e9e9e" />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                        {video.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Duración: {video.duration || '—'}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        color: '#4DABA4',
                        fontWeight: 700,
                        minWidth: 56,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {video.duration || '—'}
                    </Typography>
                    {video.videoUrl && (
                      <Button
                        href={video.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ color: '#4DABA4', fontWeight: 600, textTransform: 'none' }}
                      >
                        Ver
                      </Button>
                    )}
                  </Box>
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVideoModal}>Cancelar</Button>
          <Button
            onClick={handleVideoSelection}
            className="background-green-aqua"
            variant="contained"
            disabled={!videoModalSelection}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={documentModalOpen} onClose={closeDocumentModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Seleccionar material (PDF, Audio, Link)
          <IconButton onClick={closeDocumentModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button variant="contained" sx={{ mr: 2, background: '#4DABA4', color: '#fff', fontWeight: 600 }}>
              Buscar
            </Button>
            <TextField
              placeholder="Filtra por el nombre del material"
              value={documentSearch}
              onChange={e => setDocumentSearch(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {documentLibrary
              .filter((d) => (d.title || '').toLowerCase().includes(documentSearch.toLowerCase()))
              .map((doc) => {
                const selected = documentModalSelection && documentModalSelection.id === doc.id;
                const previewUrl =
                  doc.contentType === 'link'
                    ? doc.externalUrl
                    : resolveMediaUrl(doc.path);
                return (
                  <Box
                    key={doc.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      mb: 1,
                      borderRadius: 1,
                      background: selected ? '#b6e2d8' : '#fff',
                      border: selected ? '2px solid #4DABA4' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                    }}
                    onClick={() => setDocumentModalSelection(doc)}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 40,
                        flexShrink: 0,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: '#eee',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {doc.thumbnail ? (
                        <Box
                          component="img"
                          src={doc.thumbnail}
                          alt={doc.title}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <FolderIcon size={20} color="#9e9e9e" />
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 600 }} noWrap>
                        {doc.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.type}
                      </Typography>
                    </Box>
                    {previewUrl && (
                      <Button
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ color: '#4DABA4', fontWeight: 600, textTransform: 'none' }}
                      >
                        Ver
                      </Button>
                    )}
                  </Box>
                );
              })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDocumentModal}>Cancelar</Button>
          <Button
            onClick={() => {
              if (currentDocumentRow !== null && documentModalSelection) {
                const newDocs = [...documents];
                while (newDocs.length <= currentDocumentRow) {
                  newDocs.push({ name: '', file: null, selectedItem: null, source: null });
                }
                newDocs[currentDocumentRow] = {
                  ...newDocs[currentDocumentRow],
                  name: documentModalSelection.title,
                  selectedItem: documentModalSelection,
                  source: 'existente',
                  file: null,
                  thumbnail: documentModalSelection.thumbnail || null,
                  contentType: documentModalSelection.contentType || 'document',
                };
                setDocuments(newDocs);
              }
              setDocumentModalOpen(false);
            }}
            className="background-green-aqua"
            variant="contained"
            disabled={!documentModalSelection}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={questionnaireModalOpen} onClose={closeQuestionnaireModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Seleccionar Cuestionarios
          <IconButton onClick={closeQuestionnaireModal}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleccione los cuestionarios que desea incluir en el curso:
          </Typography>

          <List>
            {questionnaires.map((questionnaire) => (
              <ListItem
                key={questionnaire.id}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: isQuestionnaireSelected(questionnaire.id) ? '#e3f2fd' : 'transparent'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isQuestionnaireSelected(questionnaire.id)}
                      onChange={() => handleQuestionnaireToggle(questionnaire)}
                      className="checkbox-green-aqua"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {questionnaire.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {questionnaire.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%' }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeQuestionnaireModal}>Cerrar</Button>
          <Button
            onClick={closeQuestionnaireModal}
            className="background-green-aqua"
            variant="contained"
          >
            Confirmar Selección ({selectedQuestionnaires.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Progress Feedback */}
      <ProgressFeedback
        isLoading={isLoading}
        progress={progress}
        alert={alert}
        onCloseAlert={() => setAlert({ ...alert, open: false })}
        loadingMessage={editMode ? "Actualizando curso..." : "Creando curso..."}
      />
    </Collapse>
  );
};

export default NewCourseForm;